<template>
    <div class="relative inline-flex" v-click-outside="close">
        <a
            href="#"
            @click.prevent="actionsOpen = !actionsOpen"
            aria-haspopup="true"
            :aria-expanded="actionsOpen"
            role="button"
            class="btn-base rounded-full w-10 h-10 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-slate-700 outline-none"
            id="hs-mypoints-program-options"
        >
            <span>
                <i :class="['far fa-ellipsis-h text-xl', iconClass]"></i>
            </span>
        </a>
        <div
            class="origin-top-right z-10 absolute top-1/2 right-0 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden mt-4 shadow-sm dark:shadow-slate-900/50 flex w-60 p-2 items-start"
            v-show="actionsOpen"
        >
            <div class="flex flex-col items-start flex-1">
                <!-- Update Points -->
                <div
                    v-if="canUpdatePoints"
                    class="flex items-start self-stretch"
                >
                    <a
                        href="#"
                        @click.prevent="handleEditProgram(program, user)"
                        id="hs-mypoints-update-program"
                        class="flex px-2 py-1.5 items-center gap-2 flex-1 rounded bg-transparent no-underline hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                        <Icon icon="edit" class="w-4 h-4 text-slate-700 dark:text-slate-300" />
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5"
                        >
                            Update points
                        </span>
                    </a>
                </div>

                <!-- Points Update Settings -->
                <div
                    v-if="program.connected"
                    class="flex items-start self-stretch mt-1"
                >
                    <a
                        href="#"
                        @click.prevent="editProgramOptions(program, user)"
                        id="hs-mypoints-program-settings"
                        class="flex px-2 py-1.5 items-center gap-2 flex-1 rounded bg-transparent no-underline hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                        <Icon icon="settings" class="w-4 h-4 text-slate-700 dark:text-slate-300" />
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5"
                        >
                            Settings
                        </span>
                    </a>
                </div>

                <!-- About Program -->
                <div
                    v-if="canViewAboutProgram"
                    class="flex items-start self-stretch mt-1"
                >
                    <a
                        :href="transferPartnersPageUrl"
                        @click="close"
                        id="hs-mypoints-about-program"
                        class="flex px-2 py-1.5 items-center gap-2 flex-1 rounded bg-transparent no-underline hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                        <Icon icon="info" class="w-4 h-4 text-slate-700 dark:text-slate-300" />
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5"
                        >
                            About program
                        </span>
                    </a>
                </div>

                <!-- Delete Program -->
                <div
                    v-if="!program.connected"
                    class="flex items-start self-stretch mt-1"
                >
                    <span
                        role="button"
                        @click.prevent="showDeleteModal = true"
                        id="hs-mypoints-remove-program"
                        class="flex px-2 py-1.5 items-center gap-2 flex-1 rounded bg-transparent cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                        <Icon icon="trash" class="w-4 h-4 text-slate-700 dark:text-slate-300" />
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5"
                        >
                            Remove program
                        </span>
                    </span>
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <ConfirmationDialog
            v-if="showDeleteModal"
            :show="showDeleteModal"
            :title="`Delete ${program.name} points`"
            :dialog="`You are about to delete your ${program.name} points. This action cannot be undone.`"
            :buttons="[
                {
                    text: 'Cancel',
                    value: false,
                    type: 'cancel',
                    classes: 'border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-100',
                },
                {
                    text: 'Delete program points',
                    value: true,
                    type: 'confirm',
                    classes: '!bg-red-500 text-white',
                },
            ]"
            @cancel="closeDeleteModal"
            @confirm="confirmDelete"
        >
            <template #title>
                <div class="flex items-center gap-3 self-stretch">
                    <div
                        class="flex items-center justify-center w-12 h-12 p-3 rounded-full bg-red-100"
                    >
                        <Icon icon="trash" pathClass="fill-red-500" />
                    </div>
                    <span
                        class="text-gray-800 dark:text-gray-100 font-inter text-xl font-medium leading-7"
                    >
                        Delete my {{ program.name }} points
                    </span>
                </div>
            </template>
        </ConfirmationDialog>
    </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { usePointsStore } from '@/store/points';
import { useAppStore } from '@/store/app';
import { notify } from 'notiwind';

import vClickOutside from 'click-outside-vue3';
import Icon from '@/components/ui/icon/icon.vue';
import ConfirmationDialog from '@/components/ui/confirmation-dialog/confirmation-dialog.vue';

export default {
    props: {
        program: Object,
        user: Object,
        iconClass: {
            type: String,
            default: 'text-slate-800',
        },
    },
    data: () => ({
        actionsOpen: false,
        showDeleteModal: false,
    }),
    computed: {
        canUpdatePoints() {
            return (
                (this.program.connected &&
                    this.program.program_user &&
                    this.program.program_user.rewards_source === 'manual') ||
                !this.program.connected
            );
        },
        canViewAboutProgram() {
            const appStore = useAppStore();
            return (
                appStore.userSettings?.features?.['transfer-partners-guide'] ===
                true
            );
        },
        transferPartnersPageUrl() {
            if (!this.program?.slug) return '#';
            return `/resources/transfer-partners-guide/${this.program.slug}`;
        },
    },
    watch: {
        actionsOpen(val) {
            if (!val) {
                this.showDeleteModal = false;
            }
        },
    },
    directives: {
        ClickOutside: vClickOutside.directive,
    },
    components: {
        Icon,
        ConfirmationDialog,
    },
    methods: {
        ...mapActions(usePointsStore, [
            'deleteProgram',
            'editProgram',
            'setProgramModal',
            'setEditProgramModal',
            'showProgramModalAction',
            'editProgramOptions',
        ]),
        async confirmDelete() {
            try {
                await this.deleteProgram(this.program);
                this.closeDeleteModal();
            } catch (error) {
                console.error('Error deleting program:', error);
            }
        },
        async handleEditProgram(program, user) {
            try {
                await this.editProgram(program, user);
                notify({
                    type: 'success',
                    title: 'Program updated',
                    text: 'Your program points have been updated successfully.',
                });
            } catch (error) {
                console.error('Error editing program:', error);
                notify({
                    type: 'error',
                    title: 'Update failed',
                    text: 'There was an error updating your program points. Please try again.',
                });
            }
        },
        closeDeleteModal() {
            this.showDeleteModal = false;
            this.actionsOpen = false;
        },
        close() {
            this.showDeleteModal = false;
            this.actionsOpen = false;
        },
    },
    mounted() {},
};
</script>
